/* eslint-disable no-plusplus */
/* eslint-disable lines-between-class-members */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import TableItem from '@appblocks/codegeneration/SettingsConfigurations/TableItem';
import {
    CodeBlock,
    BlockType,
    SettingType,
    ValidationMessage,
    ValidationMessageType,
    Define,
    SettingControlType,
} from '@appblocks/codegeneration/types';
import { TableFieldLegacy } from '@appblocks/codegeneration/SettingsConfigurations/TableField';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';
import * as tios from './tios';
import * as simulator from './simulator';

interface TableObject {
    TBL_NAME: string;
    MAX_RECS: string;
    TBL_STRUCT: string;
    NUM_KEY_FIELD: string;
    TBL_COMMENT: string;
    FieldsDefnition: TableFieldLegacy[];
}

type TableJson = {
    name: string;
    maxRecords: number;
    tableStructure: string;
    keyFields: number;
    comment: string;
    fields: {
        name: string;
        fieldType: string;
        p1: number;
        p2: number;
        displayName: string;
        fieldDefault: string;
    }[];
};

const LOG_TEMPLATE: any = {
    id: 'tbl_table',
    settings: {
        NAME: 'LOG',
        MAXRECORDS: 200,
        STRUCT: 'L',
        FIELDS: [
            {
                id: 'tbl_field',
                settings: {
                    NAME: 'timestamp',
                    FIELDTYPE: 'E',
                    P1: '0',
                    P2: '2147483647',
                },
            },
            {
                id: 'tbl_field',
                settings: {
                    NAME: 'value',
                    FIELDTYPE: 'S',
                    P1: '0',
                    P2: '122',
                },
            },
        ],
    },
};

const DEBUG_LOG_TEMPLATE: any = {
    id: 'tbl_table',
    settings: {
        NAME: 'DEBUG',
        MAXRECORDS: 400,
        STRUCT: 'L',
        FIELDS: [
            {
                id: 'tbl_field',
                settings: {
                    NAME: 'timestamp',
                    FIELDTYPE: 'E',
                    P1: '0',
                    P2: '2147483647',
                },
            },
            {
                id: 'tbl_field',
                settings: {
                    NAME: 'value',
                    FIELDTYPE: 'S',
                    P1: '0',
                    P2: '122',
                },
            },
        ],
    },
};

export default class Tbl extends BaseFeature {
    constructor() {
        super();
        this.id = 'tbl';
        this.name = 'Data Tables';
        this.icon = 'HiOutlineDatabase';
        this.priority = 999;
        this.files = [
            'tables.xtxt',
            'libraries/tables/tables.tbh',
            'libraries/tables/tables.tbs',
        ];
        this.settings = this.settings.concat([
            {
                id: 'ENABLED',
                name: 'Data Tables Enabled',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'TBL_DEBUG_PRINT',
                name: 'Debug Print',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Enable',
                        value: '1',
                    },
                    {
                        name: 'Disable',
                        value: '0',
                    },
                ],
            },
            {
                id: 'WEBENABLED',
                name: 'Web Interface',
                settingType: SettingType.HIDDEN,
                readonly: true,
                required: true,
                default: '1',
                options: [
                    {
                        name: 'Enable',
                        value: '1',
                    },
                    {
                        name: 'Disable',
                        value: '0',
                    },
                ],
            },
            {
                id: 'TABLES',
                name: 'Data Tables',
                settingType: SettingType.ARRAY,
                required: true,
                refId: 'tbl_table',
                controlType: SettingControlType.TABS,
                default: [],
                helpText: 'Data Tables are used in blocks to store and look up data and can be consulted via the Device Console.',
            },
        ]);
    }

    getDependencies() {
        const dependencies = [];
        dependencies.push('fd');
        // if (this.getSettingValue('WEBENABLED') === '1') {
        //     dependencies.push('web_dashboard');
        // }
        return dependencies;
    }

    getDefines() {
        const TABLES = this.getSettingValue('TABLES');
        let maxNumFiles = TABLES.length + 5;
        if (maxNumFiles < 10) {
            maxNumFiles = 10;
        }
        return [
            {
                name: 'FILENUM_MAX_FILES',
                value: maxNumFiles.toString(),
            }
        ]
    }

    getFiles(): Array<string> {
        let { files } = this;
        if (this.getSettingValue('WEBENABLED') === '1') {
            files = files.concat([
                'libraries/tables/tables_web.tbh',
                'libraries/tables/tables_web.tbs',
            ]);
        }
        return files;
    }

    getDebugLogEnabled(): boolean {
        const generalFeature = this.project.getFeature('general');
        for (let i = 0; i < this.project.features.length; i++) {
            const feature = this.project.features[i];
            if (feature.id === 'linked_setting') {
                if (feature.getSettingValue('FEATURE') === generalFeature.getSettingValue('ID')
                    && feature.getSettingValue('SETTINGID') === 'LOGGING') {
                    return true;
                }
            }
        }

        if (generalFeature.getSettingValue('LOGGING', true) === 'tbl_log') {
            return true;
        }

        return false;
    }

    getEnabled(): boolean {
        const logFeature = this.project.getFeature('log');
        const logEnabled = logFeature ? logFeature.getEnabled() : false;
        const debugLogEnabled = this.getDebugLogEnabled();
        const TABLES = this.getSettingValue('TABLES');
        return TABLES.length > 0 || logEnabled || debugLogEnabled;
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const logFeature = this.project.getFeature('log');
        const logEnabled = logFeature ? logFeature.getEnabled() : false;
        const debugLogEnabled = this.getDebugLogEnabled();
        let logID = '';
        let debugLogID = '';
        const TABLES = this.getSettingValue('TABLES');
        for (let i = 0; i < TABLES.length; i += 1) {
            const tableEntry = TABLES[i];
            const codyFieldsArray = tableEntry.getSettingValue('FIELDS');
            const fields = [];
            let recordSize = 1; // active flag is 1 byte long
            for (let j = 0; j < codyFieldsArray.length; j += 1) {
                const field = {
                    DEFVAL: codyFieldsArray[j].getSettingValue('DEFAULT'),
                    FIELD_NAME: codyFieldsArray[j].getSettingValue('NAME'),
                    FIELD_TYPE: codyFieldsArray[j].getSettingValue('FIELDTYPE'),
                    FLD_COMMENT: codyFieldsArray[j].getSettingValue('COMMENT'),
                    length: 0,
                    P1: codyFieldsArray[j].getSettingValue('P1'),
                    P2: codyFieldsArray[j].getSettingValue('P2'),
                };
                fields.push(field);
                recordSize
                    += Tbl.getFieldSize(field.FIELD_TYPE,
                        Number(field.P1), Number(field.P2));
            }

            const table = {
                TBL_NAME: tableEntry.getSettingValue('NAME'),
                MAX_RECS: tableEntry.getSettingValue('MAXRECORDS'),
                TBL_STRUCT: tableEntry.getSettingValue('STRUCT'),
                NUM_KEY_FIELD: tableEntry.getSettingValue('KEYFIELDS'),
                TBL_COMMENT: tableEntry.getSettingValue('COMMENT'),
                FieldsDefinition: fields,
            };

            //= ==Pre-process, ALL VARIABLE MUST BE WELL DEFINED
            let field_index;
            const tbl_name = table.TBL_NAME;
            if (table.NUM_KEY_FIELD === '') {
                errors.push({
                    message: 'Number of key fields must be set',
                    messageType: ValidationMessageType.ERROR,
                });
            }
            // //= ==Postprocess(table loop)

            if (recordSize > 128) {
                errors.push({
                    featureId: tableEntry.getID(),
                    settingId: 'FIELDS',
                    message: `Fields total maximum size of table ${table.TBL_NAME} must not exceed 128 bytes. Current is ${recordSize}.`,
                    messageType: ValidationMessageType.ERROR,
                });
            }

            if (table.FieldsDefinition.length === 0) {
                errors.push({
                    featureId: tableEntry.getID(),
                    settingId: 'FIELDS',
                    message: 'Table requires at least one field',
                    messageType: ValidationMessageType.ERROR,
                });
            }

            if (table.TBL_STRUCT === 'L') {
                if (tableEntry.getSettingValue('NAME') === LOG_TEMPLATE.settings.NAME) {
                    logID = tableEntry.getSettingValue('ID');
                }
                if (tableEntry.getSettingValue('NAME') === DEBUG_LOG_TEMPLATE.settings.NAME) {
                    debugLogID = tableEntry.getSettingValue('ID');
                }
            }
        }
        if (logID.length === 0 && logEnabled) {
            errors.push(this.addLog('TABLES', 'tbl_table', this, LOG_TEMPLATE));
        }
        if (logID.length > 0 && !logEnabled) {
            const item = this.removeLog(logID);
            if (item) {
                errors.push(item);
            }
        }
        if (debugLogID.length === 0 && debugLogEnabled) {
            errors.push(this.addLog('TABLES', 'tbl_table', this, DEBUG_LOG_TEMPLATE));
        }
        if (debugLogID.length > 0 && !debugLogEnabled) {
            const item = this.removeLog(debugLogID);
            if (item) {
                errors.push(item);
            }
        }

        return errors;
    }

    addLog(
        settingId: string,
        refId: string,
        feature: BaseFeature,
        settingsTemplate: { name: string, value: any }[],
    ): ValidationMessage {
        return ({
            featureId: this.getID(),
            settingId: 'TABLES',
            message: '',
            messageType: ValidationMessageType.ADD,
            data: settingsTemplate,
        });
    }

    removeLog(featureId: string): ValidationMessage | undefined {
        const arr = this.getSettingValue('TABLES');
        for (let i = 0; i < arr.length; i += 1) {
            if (arr[i].getID() === featureId) {
                return ({
                    featureId,
                    settingId: undefined,
                    message: '',
                    messageType: ValidationMessageType.REMOVE,
                });
            }
        }
    }

    static getProperRecordSize(newRecordSize: number) {
        let aa = 0;
        if (newRecordSize <= 2) { aa = 2; return aa; }
        if (newRecordSize <= 4) { aa = 4; return aa; }
        if (newRecordSize <= 8) { aa = 8; return aa; }
        if (newRecordSize <= 16) { aa = 16; return aa; }
        if (newRecordSize <= 32) { aa = 32; return aa; }
        if (newRecordSize <= 64) { aa = 64; return aa; }
        if (newRecordSize <= 128) { aa = 128; return aa; }
        if (newRecordSize > 128) { aa = 255; return aa; }
        return aa;
    }

    static getFieldSize(fieldType: string, fieldP1: number, fieldP2: number): number {
        let size: number = 0;

        switch (fieldType) {
            case 'T':
                switch (fieldP1) {
                    case 0:
                        size = 2;
                        // EN_TBL_DT_DATE,'YYYYMMDD (year,month,date)
                        break;
                    case 1:
                        size = 2;
                        // EN_TBL_DT_TIME1,'hhmm (hour,minutes)
                        break;
                    case 2:
                        size = 3;
                        // EN_TBL_DT_TIME2,'hhmmss (hour,minutes,second)
                        break;
                    case 3:
                        size = 5;
                        // EN_TBL_DT_TIME3,'hhmmssmls (hour,minutes,second,milsecond)
                        break;
                    case 4:
                        size = 4;
                        // EN_TBL_DT_DATE_TIME1,'YYYYMMDDhhmm (year,month,date,hour,minutes)
                        break;
                    case 5:
                        size = 5;
                        // EN_TBL_DT_DATE_TIME2,'YYYYMMDDhhmmss(year,month,date,hour,minutes,second)
                        break;
                    case 6:
                        size = 7;
                        // EN_TBL_DT_ALL'YYYYMMDDhhmmssmls(year,month,date,hour,minutes,sec,milsec)
                        break;
                    default:

                        break;
                }
                break;
            case 'B': size = 1; break;
            case 'W': size = 2; break;
            case 'S': size = fieldP2 + 1; break;
            case 'U':
            case 'E':
            case 'M':
                size = 4; break;
            case 'F': size = 4; break;
            default: size = 0; break;
        }
        return size;
    }

    getDetails(): string {
        return `
## Data Tables
Data tables provides a way to store data with predefined schemas on the Flash Disk.

### Data Types
The following data types are supported:
* **String** - Text values
* **Number** - Numerical values
* **Time** - Time day
* **DateTime** - Time and Date

These blocks can be used with Data Tables:

![tables blocks](/static/img/blocks_tables.png)

        `;
    }

    static getTableJson(table: TableItem): TableJson {
        const tableName = table.getSettingValue('NAME');
        const maxRecords = table.getSettingValue('MAXRECORDS');
        const tableStruct = table.getSettingValue('STRUCT');
        const tableKeyfields = table.getSettingValue('KEYFIELDS');
        const tableComment = table.getSettingValue('COMMENT');
        const tableJson: TableJson = {
            name: tableName,
            maxRecords: Number(maxRecords),
            tableStructure: tableStruct,
            keyFields: Number(tableKeyfields),
            comment: tableComment,
            fields: [],
        };
        const fields = table.getSettingValue('FIELDS');
        for (let j = 0; j < fields.length; j += 1) {
            const field = fields[j];
            const fieldName = field.getSettingValue('NAME');
            const fieldType = field.getSettingValue('FIELDTYPE');
            const fieldP1 = Number(field.getSettingValue('P1'));
            const fieldP2 = Number(field.getSettingValue('P2'));
            const fieldDisplayName = field.getSettingValue('DISPLAY_NAME');
            let fieldDefault = field.getSettingValue('DEFAULT');
            if (fieldDefault === ''
                && (fieldType === 'F' || fieldType === 'M')) {
                fieldDefault = '0';
            }
            const fieldItem: any = {
                name: fieldName,
                fieldType,
                p1: fieldP1,
                p2: fieldP2,
                displayName: fieldDisplayName || fieldName,
                fieldDefault,
            };
            if (fieldType !== 'E' && fieldType !== 'M') {
                const enums = field.getSettingValue('ENUMS');
                if (enums.length > 0) {
                    fieldItem.enums = [];
                    for (let k = 0; k < enums.length; k += 1) {
                        const enumItem = enums[k];
                        const enumValue = enumItem.getSettingValue('VALUE');
                        fieldItem.enums.push({
                            value: enumValue,
                            label: enumItem.getSettingValue('LABEL'),
                        });
                    }
                }
            }
            tableJson.fields.push(fieldItem);
        }
        return tableJson;
    }

    getDocURL(): string {
        return `${process.env.HOME_URL}/docs/features/datatables`;
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'simulator':
                return simulator.getCodeBlocks(this);
            case 'tios':
                return tios.getCodeBlocks(this);
            default:
                return [];
        }
    }
}
